/**
 * The Lost Sales navigation — which doors, weeks and articles the planner has named
 * (`CLAUDE.md` §3.4, owner feedback F14: "how do I navigate this by store/category/etc?").
 *
 * ── WHAT THE CONTRACT LETS US NAVIGATE BY ───────────────────────────────────
 *
 * Of the post-season recovery reads, exactly one takes a dimension below the season:
 *
 *   GET /postseason/recovery/demand-panel?season&locationKeys&fiscalWeeks[&articleKeys]
 *
 * `locationKeys` and `fiscalWeeks` are REQUIRED by the platform (a curve is fitted over a
 * NAMED panel, G16) and `articleKeys` narrows further. Its cells are article × size × door ×
 * week. So the three keys below are the three parameters that read accepts, and nothing else:
 *
 *   `?store=`    → `locationKeys`   (also narrows the per-door potential table)
 *   `?week=`     → `fiscalWeeks`
 *   `?article=`  → `articleKeys`
 *
 * Category, cluster and the store-level state census are NOT here, because no read publishes
 * them — see the gap recorded in `CHANGELOG.d/postseason.md`. Summing a category out of an
 * article list the screen invented would be the unbounded read §3.4 exists to close.
 *
 * ── THE RULES ───────────────────────────────────────────────────────────────
 *
 * 1. **No default.** An empty key is "not narrowed", never "the first door". For the panel,
 *    whose platform read has no "all" for doors or weeks, an empty `store` or `week` means the
 *    panel is not asked for at all — the screen says what to name instead.
 * 2. **Several values, one key.** `?store=0101,0102` and `?store=0101&store=0102` read the
 *    same; the writer emits the comma form so a link stays short.
 * 3. **Parse from a string.** `readLostSalesScope` is pure; the hook is the only thing that
 *    touches `window`, and the export resolver hands it `window.location.search`.
 */

import { useEffect, useMemo, useState } from 'react';

export const STORE_PARAM = 'store';
export const WEEK_PARAM = 'week';
export const ARTICLE_PARAM = 'article';

export interface LostSalesScope {
  /** Door keys named by the planner. Empty: not narrowed. */
  readonly stores: readonly string[];
  /** Fiscal-week labels, as the platform writes them. Empty: not narrowed. */
  readonly weeks: readonly string[];
  /** Article keys. Empty: not narrowed. */
  readonly articles: readonly string[];
}

/** The list a free-text field or a query-string value names: split on commas, trimmed, de-duplicated. */
export function parseList(raw: string): readonly string[] {
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const value = part.trim();
    if (value !== '') seen.add(value);
  }
  return [...seen];
}

function readList(params: URLSearchParams, name: string): readonly string[] {
  return parseList(params.getAll(name).join(','));
}

/** The scope a search string states. Pure: no `window`, no defaults, no guesses. */
export function readLostSalesScope(search: string): LostSalesScope {
  const params = new URLSearchParams(search);
  return {
    stores: readList(params, STORE_PARAM),
    weeks: readList(params, WEEK_PARAM),
    articles: readList(params, ARTICLE_PARAM),
  };
}

/**
 * The demand-panel query the scope names, or `null` when it names no doors or no weeks — the
 * platform requires both, and has no "every door" to fall back on.
 */
export function panelQuery(
  season: string,
  scope: LostSalesScope,
): { readonly season: string; readonly locationKeys: string[]; readonly fiscalWeeks: string[]; readonly articleKeys?: string[] } | null {
  if (scope.stores.length === 0 || scope.weeks.length === 0) return null;
  return {
    season,
    locationKeys: [...scope.stores],
    fiscalWeeks: [...scope.weeks],
    ...(scope.articles.length === 0 ? {} : { articleKeys: [...scope.articles] }),
  };
}

/** The search string with the scope patched onto it; every other key is kept. Pure. */
export function withLostSalesScope(search: string, patch: Partial<LostSalesScope>): string {
  const next = new URLSearchParams(search);
  const write = (name: string, values: readonly string[] | undefined): void => {
    if (values === undefined) return;
    next.delete(name);
    if (values.length > 0) next.set(name, values.join(','));
  };
  write(STORE_PARAM, patch.stores);
  write(WEEK_PARAM, patch.weeks);
  write(ARTICLE_PARAM, patch.articles);
  return next.toString();
}

/**
 * Write the scope into the address bar. `pushState` plus a manual `popstate`: narrowing to a
 * door is a drill, and the back button should walk it (the same note as ch05's scope writer).
 */
export function setLostSalesScope(patch: Partial<LostSalesScope>): void {
  if (typeof window === 'undefined') return;
  const query = withLostSalesScope(window.location.search, patch);
  window.history.pushState({}, '', query === '' ? window.location.pathname : `?${query}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** The current scope, following browser back and forward. */
export function useLostSalesScope(): LostSalesScope {
  const [search, setSearch] = useState(() => (typeof window === 'undefined' ? '' : window.location.search));

  useEffect(() => {
    const onPopState = (): void => setSearch(window.location.search);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return useMemo(() => readLostSalesScope(search), [search]);
}
